import { useEffect, useRef, type ReactNode } from 'react';
import {
  Animated,
  Pressable,
  StyleSheet,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { IconButton } from '../index';
import { focusLandedRow } from '../primitives/dismissSoftKeyboard';
import type { useTheme } from './theme';
import { useAdvanceFlash } from './useAdvanceFlash';

// One tappable timeline set-row card, extracted from TimelineList so it can own
// a per-row flash hook (2026-07-20). A just-logged FAILED set flashes this card
// briefly — border + background pulse via useAdvanceFlash (the same one-shot the
// FocusPanel uses on advance) — instead of the old persistent danger banner: a
// fail is a normal outcome, so it gets the ordinary "here's what landed" flash,
// not an alarm. `flashKey` is 0 for every row except the just-failed one, which
// receives a bumping key so the hook re-fires (Rules of Hooks: each row is its
// own component instance, so the hook count stays stable across the feed).
//
// The card's resting colours are surface / border — exactly useAdvanceFlash's
// rest values — so a non-flashing row (flashKey 0, hook skips its effect) renders
// identically to before the extraction.
//
// ⭐ LG-67 — the card gained a second action, so the single card-wide Pressable
// became a BODY Pressable plus a ✕, siblings inside the flashing card. That is
// the shape TimelineNoteCard has always had two rows further down this same
// list, and it is the shape a11y needs: a ✕ nested INSIDE the card's Pressable
// would be a touchable inside a touchable, which a screen reader reads as one
// control with two jobs. The visible cost, stated rather than hidden: the press
// tint now covers the body instead of the whole card, because the card is no
// longer the thing being pressed. The grouped left-stripe is unchanged.
//
// #225: `focusKey` moves focus onto the body when a completing tap lands here.
export function TimelineSetRow({
  onPress,
  onUncomplete,
  uncompleteLabel,
  grouped,
  flashKey,
  focusKey,
  palette,
  theme,
  rowStyle,
  groupedStyle,
  pressedStyle,
  children,
}: {
  onPress: () => void;
  /** Un-complete this set (LG-67): it leaves the timeline and goes back to
   *  pending, re-loggable, with the typed values intact. Optional — a mount
   *  that has no draft to write to (a read-only feed) renders no ✕ rather than
   *  a button that does nothing. */
  onUncomplete?: () => void;
  /** Localized spoken label for the ✕. Required whenever `onUncomplete` is
   *  given; the list owns the strings, this row owns the pixels. */
  uncompleteLabel?: string;
  grouped: boolean;
  /** 0 = never flashed; a bumping positive number = this is the just-failed
   *  row, flash now (and again if it re-fires). */
  flashKey: number;
  /** Bumping positive number = a completing tap landed here; take focus on a
   *  touch browser (#225). 0 or absent = nothing. */
  focusKey?: number;
  palette: ReturnType<typeof useTheme>['palette'];
  theme: ReturnType<typeof useTheme>;
  rowStyle: StyleProp<ViewStyle>;
  groupedStyle: StyleProp<ViewStyle>;
  pressedStyle: StyleProp<ViewStyle>;
  children: ReactNode;
}) {
  const { flashBg, flashBorder } = useAdvanceFlash(flashKey, palette);
  const styles = makeStyles(theme);
  const bodyRef = useRef(null);
  useEffect(() => {
    if (focusKey) focusLandedRow(bodyRef.current);
  }, [focusKey]);
  return (
    <Animated.View
      style={[
        rowStyle,
        grouped && groupedStyle,
        { backgroundColor: flashBg, borderColor: flashBorder },
      ]}
    >
      <Pressable
        ref={bodyRef}
        accessibilityRole="button"
        onPress={onPress}
        style={({ pressed }) => [styles.body, pressed && pressedStyle]}
      >
        {children}
      </Pressable>
      {onUncomplete && uncompleteLabel ? (
        <IconButton
          theme={theme}
          name="close"
          // `cancel` (#216, Antenore): the column of ✕ weighed too much with
          // many sets. The boundary with the pressable row beside it is kept
          // by the no-LEFT-slop rule below, not by the border strength.
          // Not destructive: un-completing is undone by redoing the set.
          variant="cancel"
          accessibilityLabel={uncompleteLabel}
          onPress={onUncomplete}
          // ⛔ No LEFT slop, and it is the whole safety of this row. The default
          // is 8 on every side and the gap to the body is `s.s3` — also 8 — so
          // the default would put the ✕'s touch zone flush against a control
          // that is ALSO pressable, with no buffer at all. A thumb that
          // overshoots the row by a few pixels would then un-complete a set
          // that was right, which costs more than the mistake the ✕ exists to
          // undo. IconButton documents this exact case ("pass {top,bottom} in
          // tight horizontal rows"); the box stays 36 and 36 + 8 right keeps
          // the ≥44 floor without borrowing from the neighbour.
          // Measured, not assumed: a11y audit of this changeset, 2026-09-04.
          hitSlop={{ top: 8, bottom: 8, left: 0, right: 8 }}
        />
      ) : null}
    </Animated.View>
  );
}

// The body carries the row direction + the gap that `styles.row` used to give
// its two children directly; the card keeps the padding and now spaces
// [body, ✕] instead. `alignItems` stays flex-start so a two-line row's icon and
// ✕ sit level with the first line, not centred against the whole card.
function makeStyles(theme: ReturnType<typeof useTheme>) {
  return StyleSheet.create({
    body: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: theme.spacing.s3,
    },
  });
}
